import { FC, useMemo, useState } from "react"
import { Box, Button, Tooltip } from "@mui/material"
import { Add, DeleteOutlined, Edit } from "@mui/icons-material"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { GuestBot } from "../../interfaces"
import { useTemplatesListQuery, useDeleteTemplateMutation } from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import { TemplateForm } from "../forms"
import { TooltipAction } from "../common"

const TYPE_LABEL: Record<GuestBot.PayloadType, string> = {
  text: "Текст",
  photo: "Фото",
  document: "Документ",
}

export const TemplatesTable: FC = () => {
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const { data, isFetching } = useTemplatesListQuery({
    queryParams: { limit: pageSize, offset: page * pageSize },
  })
  const rows = data?.items ?? []
  const canWrite = useGuestBotWriteAccess()
  const [deleteTemplate] = useDeleteTemplateMutation()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<GuestBot.Template | null>(null)
  const [error, setError] = useState<string | null>(null)

  const columns: GridColDef<GuestBot.Template>[] = useMemo(
    () => [
      { field: "name", headerName: "Название", flex: 2 },
      {
        field: "type",
        headerName: "Тип",
        flex: 1,
        valueGetter: (params) => TYPE_LABEL[params.row.payload.type],
      },
      {
        field: "preview",
        headerName: "Содержимое",
        flex: 3,
        valueGetter: (params) =>
          params.row.payload.text ?? params.row.payload.media ?? "",
      },
    ],
    [],
  )

  const rowActions = useMemo(() => {
    if (!canWrite) return []
    return [
      (params: GridRowParams<GuestBot.Template>) => (
        <TooltipAction
          icon={<Edit />}
          label="Редактировать"
          onClick={() => setEditing(params.row)}
        />
      ),
      (params: GridRowParams<GuestBot.Template>) => (
        <TooltipAction
          icon={<DeleteOutlined />}
          label="Удалить"
          onClick={async () => {
            const res = await deleteTemplate({
              pathParams: { id: params.row.id },
            })
            if ("error" in res) {
              const status = (res.error as { status?: number })?.status
              setError(
                status === 409
                  ? "Шаблон используется в привязках"
                  : "Не удалось удалить шаблон",
              )
            }
          }}
        />
      ),
    ]
  }, [canWrite, deleteTemplate])

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Tooltip title="Создать новый шаблон ответа" arrow>
            <Button startIcon={<Add />} onClick={() => setCreating(true)}>
              Создать шаблон
            </Button>
          </Tooltip>
        </Box>
      )}
      <Box height="70vh">
        <DataGrid<GuestBot.Template>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={isFetching}
          paginationMode="server"
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          rowActions={rowActions}
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новый шаблон"
          onClose={() => setCreating(false)}
          element={<TemplateForm onDone={() => setCreating(false)} />}
        />
      )}
      {editing && (
        <Modal
          disableBackdropClose
          title="Редактирование шаблона"
          onClose={() => setEditing(null)}
          element={
            <TemplateForm template={editing} onDone={() => setEditing(null)} />
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!error}
        onClose={() => setError(null)}
        variant="error"
        message={error ?? ""}
      />
    </>
  )
}
